import { useIntl } from 'react-intl';
import { EuiButtonIcon, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFormLabel, EuiText } from '@elastic/eui';
import { publishDateFromISO8601, publishDateToISO8601 } from 'cache';
import { useReactiveVar } from '@apollo/client';
import moment from 'moment';

import * as S from './FiltersSidebar.styles';

const isoToDateInputValue = (iso: string | null): string => {
  if (!iso) return '';
  const m = moment(iso);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

export const PublishDateFilter = (): JSX.Element => {
  const { formatMessage } = useIntl();
  const fromDate = useReactiveVar(publishDateFromISO8601);
  const toDate = useReactiveVar(publishDateToISO8601);

  const fromValue = isoToDateInputValue(fromDate);
  const toValue = isoToDateInputValue(toDate);
  const today = moment().format('YYYY-MM-DD');

  return (
    <S.SidebarFilterWrapper>
      <EuiFormLabel>{formatMessage({ id: 'filters.publishDate.label', defaultMessage: 'Publish date' })}</EuiFormLabel>
      <EuiFlexGroup gutterSize="s" direction="column" style={{ marginTop: 4 }}>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiText size="xs" style={{ minWidth: 16 }}>
                {formatMessage({ id: 'filters.publishDate.from', defaultMessage: 'From' })}
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFieldText
                fullWidth
                compressed
                type="date"
                value={fromValue}
                max={toDate ? isoToDateInputValue(toDate) : today}
                aria-label={formatMessage({ id: 'filters.publishDate.from', defaultMessage: 'From' })}
                onChange={(e): void => {
                  const v = e.target.value;
                  if (!v) {
                    publishDateFromISO8601(null);
                    return;
                  }
                  const parsed = moment(v, 'YYYY-MM-DD', true);
                  publishDateFromISO8601(parsed.isValid() ? parsed.format('YYYY-MM-DD') : null);
                }}
              />
            </EuiFlexItem>
            {fromDate ? (
              <EuiFlexItem grow={false}>
                <EuiButtonIcon
                  size="s"
                  iconType="cross"
                  display="empty"
                  onClick={(): void => publishDateFromISO8601(null)}
                  aria-label={formatMessage({
                    id: 'filters.publishDate.clearFrom',
                    defaultMessage: 'Clear start date',
                  })}
                />
              </EuiFlexItem>
            ) : null}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiText size="xs" style={{ minWidth: 16 }}>
                {formatMessage({ id: 'filters.publishDate.to', defaultMessage: 'To' })}
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFieldText
                fullWidth
                compressed
                type="date"
                value={toValue}
                min={fromDate ? isoToDateInputValue(fromDate) : undefined}
                max={today}
                aria-label={formatMessage({ id: 'filters.publishDate.to', defaultMessage: 'To' })}
                onChange={(e): void => {
                  const v = e.target.value;
                  if (!v) {
                    publishDateToISO8601(null);
                    return;
                  }
                  const parsed = moment(v, 'YYYY-MM-DD', true);
                  publishDateToISO8601(parsed.isValid() ? parsed.format('YYYY-MM-DD') : null);
                }}
              />
            </EuiFlexItem>
            {toDate ? (
              <EuiFlexItem grow={false}>
                <EuiButtonIcon
                  size="s"
                  iconType="cross"
                  display="empty"
                  onClick={(): void => publishDateToISO8601(null)}
                  aria-label={formatMessage({
                    id: 'filters.publishDate.clearTo',
                    defaultMessage: 'Clear end date',
                  })}
                />
              </EuiFlexItem>
            ) : null}
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    </S.SidebarFilterWrapper>
  );
};
